import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Tabs from "@radix-ui/react-tabs";
import { UserCircle2, Trash2, Save, Plus, X as XIcon, TerminalSquare, GitPullRequestArrow, Shield, Sparkles, LifeBuoy, Megaphone, Cpu, Send, Wrench, Search } from "lucide-react";
import { BridgeApi, SpritesApi } from "../lib/api";
import type { SpriteJob } from "../lib/api";
import { PersonaBuilder } from "../components/PersonaBuilder";
import { qk } from "../lib/query";
import { getHashParam } from "./registry";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { MountsEditor } from "../components/MountsEditor";
import { JsonDiff } from "../components/JsonDiff";
import { ObjectHistory } from "./Agents";
import { cn } from "../lib/cn";
import type { Persona } from "../lib/types";

interface PersonaEntry { name: string; persona: Persona; tmux: boolean }

const NEW_PERSONA: Persona = { agent: "", mention_names: [] };

export default function Personas() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(() => getHashParam("p"));
  const [showBuilder, setShowBuilder] = useState(false);

  const { data: cfg, isLoading } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const { data: workspaces = {} } = useQuery({
    queryKey: qk.bridge.workspaces,
    queryFn: BridgeApi.workspaces,
    refetchInterval: 15_000,
  });
  const personas = cfg?.personas ?? {};
  const entries: PersonaEntry[] = useMemo(
    () => Object.entries(personas).map(([name, persona]) => ({
      name,
      persona,
      tmux: workspaces[name]?.tmux ?? false,
    })),
    [personas, workspaces],
  );
  const agentNames = Object.keys(cfg?.agents ?? {});


  const deleteMut = useMutation({
    mutationFn: BridgeApi.deletePersona,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      setSelected(null);
      toast.success("Persona deleted");
    },
  });

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Personas"
        icon={UserCircle2}
        subtitle={`${entries.length} personas · ${agentNames.length} agents`}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowBuilder(true)}>
              <Sparkles className="size-3.5" />
              Create with agent
            </Button>
            <Button size="sm" onClick={() => setSelected("__new__")}>
              <Plus className="size-3.5" />
              New persona
            </Button>
          </div>
        }
      />
      {showBuilder && (
        <PersonaBuilder
          onClose={() => setShowBuilder(false)}
          onCreated={(name) => { qc.invalidateQueries({ queryKey: qk.bridge.config }); setSelected(name); }}
        />
      )}
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<PersonaEntry>
          items={entries}
          itemKey={(e) => e.name}
          selectedId={selected}
          onSelect={setSelected}
          itemHeight={64}
          emptyState={<EmptyState icon={<UserCircle2 className="size-8" />} title="No personas yet" />}
          renderItem={({ item }) => <PersonaRow entry={item} />}
          renderCreating={() => (
            <PersonaEditor
              key="__new__"
              entryName=""
              persona={NEW_PERSONA}
              isCreating
              agentNames={agentNames}
              existingNames={entries.map((e) => e.name)}
              onSaved={(name) => {
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                setSelected(name);
              }}
              onDelete={() => { /* no-op for create */ }}
              onClose={() => setSelected(null)}
            />
          )}
          renderDetail={(item) => (
            <PersonaEditor
              key={item.name}
              entryName={item.name}
              persona={item.persona}
              isCreating={false}
              agentNames={agentNames}
              existingNames={entries.map((e) => e.name)}
              onSaved={(name) => {
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                setSelected(name);
              }}
              onDelete={() => {
                if (confirm(`Delete persona "${item.name}"?`)) {
                  deleteMut.mutate(item.name);
                }
              }}
              onClose={() => setSelected(null)}
            />
          )}
        />
      </div>
    </div>
  );
}

function PersonaRow({ entry }: { entry: PersonaEntry }) {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <div className="size-5 rounded-full bg-info-subtle text-info text-[10px] font-bold flex items-center justify-center">
          {entry.name.slice(0, 2).toUpperCase()}
        </div>
        <span className="text-sm font-medium text-fg truncate">{entry.name}</span>
        {entry.tmux && (
          <span
            className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-medium bg-success-subtle text-success border border-success/30"
            title="TUI session active (tmux)"
          >
            <span className="size-1 rounded-full bg-success animate-pulse" />
            TUI
          </span>
        )}
        <span className="ml-auto text-[11px] text-fg-subtle font-mono">{entry.persona.agent || "(no agent)"}</span>
      </div>
      <div className="text-xs text-fg-muted truncate">
        {entry.persona.mention_names.length > 0
          ? entry.persona.mention_names.map((n) => `@${n}`).join(" ")
          : "(no mention names)"}
      </div>
    </div>
  );
}

// Character sprite for the Visualize office scene. Generate from a keyword,
// or leave it empty to ask the persona itself (over the bus RPC layer) to
// describe its own appearance first. Model/quality/prompt live in Settings.
function SpritePanel({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: jobs = [] } = useQuery({
    queryKey: ["sprites"],
    queryFn: SpritesApi.list,
    refetchInterval: (q) => {
      const j = (q.state.data as SpriteJob[] | undefined)?.find((r) => r.persona === personaName);
      return j && (j.status === "describing" || j.status === "generating") ? 2000 : false;
    },
  });
  const job = jobs.find((j) => j.persona === personaName);
  // Mirror the server's 15-min staleness cutoff so an orphaned in-progress row
  // (server restarted mid-generation) doesn't pin the button disabled forever.
  const busy = (job?.status === "describing" || job?.status === "generating")
    && Date.now() - Date.parse(job.updated_at) < 15 * 60 * 1000;
  const [keyword, setKeyword] = useState("");

  const mut = useMutation({
    mutationFn: () => SpritesApi.generate(personaName, keyword.trim() || undefined),
    onSuccess: (j) => {
      qc.invalidateQueries({ queryKey: ["sprites"] });
      toast.success(j.status === "describing"
        ? `Asked ${personaName} to describe itself — this takes a minute or two`
        : "Generating character…");
    },
    onError: (err) => toast.error("Generate failed", String(err)),
  });

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3">
      <div className="flex items-start gap-3">
        {job?.has_png ? (
          <img
            src={SpritesApi.pngUrl(personaName, job.updated_at)}
            alt={`${personaName} sprite`}
            className="size-16 flex-shrink-0 rounded border border-border bg-surface-elevated"
            style={{ imageRendering: "pixelated" }}
          />
        ) : (
          <div className="size-16 flex-shrink-0 rounded border border-dashed border-border grid place-items-center text-fg-subtle">
            <UserCircle2 className="size-5" />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <div className="text-sm font-medium text-fg">Character sprite</div>
            <p className="text-[11px] text-fg-subtle mt-0.5">
              Pixel-art character shown on the Visualize page. Describe it with a keyword — or leave
              empty and {personaName} will be spawned to describe itself. Style &amp; model are in Settings.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={busy}
              className="flex-1 min-w-0 text-sm"
              placeholder="e.g. grumpy dwarf blacksmith with a wrench (optional)"
            />
            <Button size="sm" onClick={() => mut.mutate()} loading={mut.isPending || busy} disabled={busy}>
              <Sparkles className="size-3.5" />
              {job?.has_png ? "Regenerate" : "Generate"}
            </Button>
          </div>
          {busy && (
            <p className="text-[11px] text-info">
              {job?.status === "describing" ? `waiting for ${personaName} to describe itself…` : "drawing the sprite…"}
            </p>
          )}
          {job?.status === "error" && <p className="text-[11px] text-danger break-words">{job.error}</p>}
          {job?.status === "done" && job.description && (
            <p className="text-[11px] text-fg-subtle italic truncate" title={job.description}>“{job.description}”</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Claude session toggle. When on, the bridge runs an interactive
// `claude --dangerously-skip-permissions [--model …] [--effort …] --remote-control <name>`
// inside a detached tmux session named after the persona, in its workdir. Off kills
// the tmux session (and the claude process with it). Status polls every 5s. Model +
// effort are launch options (remembered per persona in localStorage; empty = Claude's
// own defaults — account-default model, settings.json effort=xhigh).
const TUI_MODELS = [
  { v: "", label: "Default (Opus 4.8)" },
  // Needs Claude Code >= 2.1.280 — 2.1.278 rejected it with "does not support this
  // model", which is also how the id was confirmed: the API validated the NAME and
  // refused the client. Dash-separated like every other id here; `claude-opus-5.5`
  // does not exist. Verified 2026-09-23 on 2.1.280 — canonicalModel came back
  // "claude-opus-5-5" (1M context), so it resolves to itself, not an alias.
  { v: "claude-opus-5-5", label: "Opus 5.5" },
  { v: "claude-opus-5", label: "Opus 5" },
  { v: "claude-opus-4-8", label: "Opus 4.8" },
  // Newest of a family first. Dash-separated: `claude-sonnet-5.5` is rejected ("may not
  // exist"). Verified 2026-10-07 on Claude Code 2.1.287 — it answered, and --output-format
  // json reported the model as "claude-sonnet-5-5" (1M context), so it resolves to itself.
  { v: "claude-sonnet-5-5", label: "Sonnet 5.5" },
  { v: "claude-sonnet-4-6", label: "Sonnet 4.6" },
  { v: "claude-haiku-4-5", label: "Haiku 4.5" },
  // Newest of a family first, as with Opus 5 above 4.8. The id is dash-separated:
  // `claude-fable-5.1` is rejected outright. Verified 2026-09-04 against the CLI —
  // --output-format json reported canonicalModel "claude-fable-5-1", so it resolves
  // to itself rather than aliasing to another model.
  { v: "claude-fable-5-1", label: "Fable 5.1" },
  { v: "claude-fable-5", label: "Fable 5" },
];
const TUI_EFFORTS = [
  { v: "", label: "Default (xhigh)" },
  { v: "low", label: "Low" },
  { v: "medium", label: "Medium" },
  { v: "high", label: "High" },
  { v: "xhigh", label: "xhigh" },
  { v: "max", label: "max" },
  // Not an --effort level: a session MODE (xhigh + standing dynamic-workflow
  // orchestration). The server launches it via --settings, not --effort.
  { v: "ultracode", label: "ultracode" },
];

function TuiToggle({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["persona-tui", personaName],
    queryFn: () => BridgeApi.tuiStatus(personaName),
    refetchInterval: 5000,
  });
  const running = data?.running ?? false;

  // Launch options, remembered per persona (a browser-local UI preference, not fleet config).
  const [model, setModel] = useState(() => localStorage.getItem(`tui-model:${personaName}`) ?? "");
  const [effort, setEffort] = useState(() => localStorage.getItem(`tui-effort:${personaName}`) ?? "");
  const pickModel = (v: string) => { setModel(v); localStorage.setItem(`tui-model:${personaName}`, v); };
  const pickEffort = (v: string) => { setEffort(v); localStorage.setItem(`tui-effort:${personaName}`, v); };

  const mut = useMutation({
    mutationFn: (next: boolean) =>
      next ? BridgeApi.startTui(personaName, { model: model || undefined, effort: effort || undefined }) : BridgeApi.stopTui(personaName),
    onSuccess: (_res, next) => {
      qc.invalidateQueries({ queryKey: ["persona-tui", personaName] });
      qc.invalidateQueries({ queryKey: qk.bridge.workspaces });
      toast.success(next ? `Claude session started — tmux -L agent-fleet attach -t ${personaName}` : "Claude session stopped");
    },
    onError: (err) => toast.error("Claude session toggle failed", String(err)),
  });

  const busy = mut.isPending || isLoading;
  // Match the working select in MountsEditor: h-7 fits the global form padding
  // (h-6 clipped the text); base styling supplies border/bg/rounded/focus.
  const selectCls = "h-7 text-xs disabled:opacity-50";

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3">
      <div className="flex items-center gap-3">
        <TerminalSquare className={cn("size-4 flex-shrink-0", running ? "text-success" : "text-fg-subtle")} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg flex items-center gap-2">
            Claude Session
            {running && (
              <span className="inline-flex items-center gap-1 h-4 px-1.5 rounded text-[10px] font-medium bg-success-subtle text-success border border-success/30">
                <span className="size-1 rounded-full bg-success animate-pulse" /> running
              </span>
            )}
          </div>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            Interactive Claude session for pair-programming, in <code className="font-mono">/data/agent-fleet/{personaName}</code>.
            Attach with <code className="font-mono">tmux -L agent-fleet attach -t {personaName}</code>.
          </p>
        </div>
        {/* Switch */}
        <button
          type="button"
          role="switch"
          aria-checked={running}
          disabled={busy}
          onClick={() => mut.mutate(!running)}
          className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ring-focus disabled:opacity-50",
            running ? "bg-success" : "bg-surface-elevated border border-border",
          )}
          aria-label={running ? "Stop Claude session" : "Start Claude session"}
        >
          <span
            className={cn(
              "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
              running ? "translate-x-6" : "translate-x-1",
            )}
          />
        </button>
      </div>

      {/* Model + effort — applied when the session starts */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 pl-7">
        <label className="flex items-center gap-1.5 text-[11px] text-fg-muted">
          Model
          <select value={model} onChange={(e) => pickModel(e.target.value)} disabled={busy} className={selectCls}>
            {TUI_MODELS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-fg-muted">
          Effort
          <select value={effort} onChange={(e) => pickEffort(e.target.value)} disabled={busy} className={selectCls}>
            {TUI_EFFORTS.map((e2) => <option key={e2.v} value={e2.v}>{e2.label}</option>)}
          </select>
        </label>
        {running && <span className="text-[10px] text-fg-subtle">restart the session to apply changes</span>}
      </div>
    </div>
  );
}

function DelegateToggle({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["delegate-allowlist"],
    queryFn: () => BridgeApi.delegateAllowlist(),
  });
  const allowed = data?.agents.includes(personaName) ?? false;

  const mut = useMutation({
    mutationFn: (next: boolean) => BridgeApi.setDelegate(personaName, next),
    onSuccess: (_res, next) => {
      qc.invalidateQueries({ queryKey: ["delegate-allowlist"] });
      toast.success(next ? `${personaName} may now delegate coding` : `${personaName} can no longer delegate`);
    },
    onError: (err) => toast.error("Toggle failed", String(err)),
  });
  const busy = mut.isPending || isLoading;

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3">
      <div className="flex items-center gap-3">
        <GitPullRequestArrow className={cn("size-4 flex-shrink-0", allowed ? "text-accent" : "text-fg-subtle")} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg">May delegate coding</div>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            Allows this agent to call the <code className="font-mono">delegate</code> tools — offloading coding
            tasks to a fresh agent in an isolated worktree. The worker runs arbitrary commands on this host, so
            grant it only to agents you trust with that.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={allowed}
          disabled={busy}
          onClick={() => mut.mutate(!allowed)}
          className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ring-focus disabled:opacity-50",
            allowed ? "bg-accent" : "bg-surface-elevated border border-border",
          )}
          aria-label={allowed ? "Revoke delegate capability" : "Grant delegate capability"}
        >
          <span
            className={cn(
              "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
              allowed ? "translate-x-6" : "translate-x-1",
            )}
          />
        </button>
      </div>
    </div>
  );
}

function EscalateToggle({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["escalation-allowlist"],
    queryFn: () => BridgeApi.escalationAllowlist(),
  });
  const allowed = data?.agents.includes(personaName) ?? false;

  const mut = useMutation({
    mutationFn: (next: boolean) => BridgeApi.setEscalation(personaName, next),
    onSuccess: (_res, next) => {
      qc.invalidateQueries({ queryKey: ["escalation-allowlist"] });
      toast.success(next ? `${personaName} can now hand off to you` : `${personaName} handoff disabled`);
    },
    onError: (err) => toast.error("Toggle failed", String(err)),
  });
  const busy = mut.isPending || isLoading;

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3">
      <div className="flex items-center gap-3">
        <LifeBuoy className={cn("size-4 flex-shrink-0", allowed ? "text-accent" : "text-fg-subtle")} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg">Handoff &amp; approval</div>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            Gives this persona the <code className="font-mono">escalate</code> + <code className="font-mono">request_approval</code> tools —
            it can pull you in (muting itself in that chat) or ask before a risky action. Notices go to your
            notification routes. Leave off for personas where a human handoff makes no sense (e.g. your own assistant).
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={allowed}
          disabled={busy}
          onClick={() => mut.mutate(!allowed)}
          className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ring-focus disabled:opacity-50",
            allowed ? "bg-accent" : "bg-surface-elevated border border-border",
          )}
          aria-label={allowed ? "Disable handoff" : "Enable handoff"}
        >
          <span
            className={cn(
              "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
              allowed ? "translate-x-6" : "translate-x-1",
            )}
          />
        </button>
      </div>
    </div>
  );
}

const minToTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const timeToMin = (t: string) => { const [h, m] = t.split(":").map(Number); return (h || 0) * 60 + (m || 0); };

// Per-persona inline tools — promote tools from "discovered via find_tools" to
// "shown first-class". Mobile-first: removable chips for the current set + a
// search box that filters promotable tools (grouped by provider) into a
// scrollable tap-to-add list, so it stays usable with 100+ candidates.
function InlineToolsPanel({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["inline-tools", personaName], queryFn: () => BridgeApi.inlineTools(personaName) });
  const mut = useMutation({
    mutationFn: (v: { tool: string; enabled: boolean }) => BridgeApi.setInlineTool(personaName, v.tool, v.enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inline-tools", personaName] }),
    onError: (e) => toast.error("Update failed", String(e)),
  });

  const inline = new Set(data?.inline ?? []);
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (data?.available ?? [])
    .filter((t) => !inline.has(t.name))
    .filter((t) => !terms.length || terms.every((term) => `${t.name} ${t.description ?? ""}`.toLowerCase().includes(term)));
  const shown = matches.slice(0, 40);
  const byProvider: Record<string, typeof shown> = {};
  for (const m of shown) (byProvider[m.provider] ??= []).push(m);

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3 space-y-3">
      <div className="flex items-start gap-3">
        <Wrench className="size-4 flex-shrink-0 text-accent mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-fg">Inline tools</div>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            Shown to {personaName} directly. Core tools (<code className="font-mono">inbox</code>, <code className="font-mono">reply_to_chat</code>, …)
            are always shown; everything else is discovered via <code className="font-mono">find_tools</code>. Promote tools this persona
            uses constantly so it skips the lookup.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {inline.size === 0 && <span className="text-[11px] text-fg-subtle">None — discovers everything via find_tools.</span>}
        {[...inline].sort().map((name) => (
          <button key={name} type="button" disabled={mut.isPending} onClick={() => mut.mutate({ tool: name, enabled: false })}
            className="inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent border border-accent/30 pl-2 pr-1 py-1 text-[11px] font-mono ring-focus"
            aria-label={`Remove ${name}`}>
            {name} <XIcon className="size-3" />
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tools to add (e.g. asana, schedule)…"
            className="w-full text-sm pl-7" />
        </div>
        {isLoading ? (
          <p className="text-[11px] text-fg-subtle">loading…</p>
        ) : (
          <div className="max-h-56 overflow-y-auto overscroll-contain rounded-md border border-border/60 divide-y divide-border/40">
            {shown.length === 0 && (
              <p className="text-[11px] text-fg-subtle p-2">{search.trim() ? "No matching tools." : "All available tools are already inline."}</p>
            )}
            {Object.entries(byProvider).map(([provider, list]) => (
              <div key={provider}>
                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-fg-subtle bg-surface-2/70 sticky top-0">{provider}</div>
                {list.map((t) => (
                  <button key={t.name} type="button" disabled={mut.isPending} onClick={() => mut.mutate({ tool: t.name, enabled: true })}
                    className="w-full text-left px-2 py-2 hover:bg-surface-elevated flex items-center gap-2 ring-focus">
                    <Plus className="size-3.5 flex-shrink-0 text-fg-subtle" />
                    <span className="min-w-0 flex-1">
                      <span className="font-mono text-xs text-fg block truncate">{t.name}</span>
                      {t.description && <span className="text-[10px] text-fg-subtle block truncate">{t.description}</span>}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
        {matches.length > 40 && <p className="text-[10px] text-fg-subtle">Showing first 40 of {matches.length} — refine your search.</p>}
      </div>
    </div>
  );
}

function CapabilitiesPanel({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["capabilities"], queryFn: () => BridgeApi.capabilities() });
  const caps = data?.capabilities[personaName] ?? { jobs: false, messaging: true };
  const mut = useMutation({
    mutationFn: (v: { cap: "jobs" | "messaging"; enabled: boolean }) => BridgeApi.setCapability(personaName, v.cap, v.enabled),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["capabilities"] });
      toast.success(`${personaName} ${v.cap} ${v.enabled ? "enabled" : "disabled"}`);
    },
    onError: (err) => toast.error("Toggle failed", String(err)),
  });

  const row = (cap: "jobs" | "messaging", Icon: typeof Cpu, title: string, desc: React.ReactNode) => {
    const on = caps[cap];
    return (
      <div className="flex items-center gap-3">
        <Icon className={cn("size-4 flex-shrink-0", on ? "text-accent" : "text-fg-subtle")} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg">{title}</div>
          <p className="text-[11px] text-fg-subtle mt-0.5">{desc}</p>
        </div>
        <button
          type="button" role="switch" aria-checked={on} disabled={mut.isPending}
          onClick={() => mut.mutate({ cap, enabled: !on })}
          className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ring-focus disabled:opacity-50", on ? "bg-accent" : "bg-surface-elevated border border-border")}
          aria-label={`${on ? "Disable" : "Enable"} ${title}`}
        >
          <span className={cn("inline-block size-4 transform rounded-full bg-white shadow transition-transform", on ? "translate-x-6" : "translate-x-1")} />
        </button>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3 space-y-3">
      {row("jobs", Cpu, "Background jobs", <>The <code className="font-mono">run_job</code> tools — run arbitrary shell in this persona's sandbox. Off for a chat persona that shouldn't execute code. New personas default off.</>)}
      <div className="border-t border-border/60" />
      {row("messaging", Send, "Cross-agent messaging", <>The <code className="font-mono">send</code> / request-response tools — message & RPC <em>other</em> agents on the bus. <code className="font-mono">reply_to_chat</code> (its own chat) is always available.</>)}
    </div>
  );
}

function ProactivityPanel({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["proactive-configs"], queryFn: () => BridgeApi.proactiveConfigs() });
  const { data: cfg } = useQuery({ queryKey: ["bridge", "config"], queryFn: () => BridgeApi.config() });
  const c = data?.configs.find((x) => x.persona === personaName)
    ?? { persona: personaName, enabled: false, mode: "ask" as const, max_per_chat: 1, max_per_day: 3, cooldown_min: 360, active_start_min: 480, active_end_min: 1260, scope: "optin" as const };
  const routes = (cfg?.routes ?? []).filter((r) => r.persona === personaName && r.match.chat_jid);

  const mut = useMutation({
    mutationFn: (patch: Partial<typeof c>) => BridgeApi.setProactive(personaName, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proactive-configs"] }),
  });
  const set = (patch: Partial<typeof c>) => mut.mutate(patch);

  // Pick by ROUTE (unique, operator-named) — two chats can resolve to the same
  // contact name ("Harry Sitorus" on WhatsApp AND Telegram), so the chat name
  // alone isn't enough to choose. Value = route name; we resolve the jid from it.
  const [testChat, setTestChat] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);
  const chatLabel = (r: typeof routes[number]) => r.chat_name ?? r.channel_name ?? r.match.chat_jid ?? r.name;
  const selectedRoute = routes.find((r) => r.name === testChat) ?? routes[0];
  const test = useMutation({
    mutationFn: () => BridgeApi.testProactive(personaName, selectedRoute?.match.chat_jid ?? ""),
    onSuccess: (r) => setTestResult(
      `${selectedRoute ? `${chatLabel(selectedRoute)}: ` : ""}` +
      (r.verdict.allow ? `✅ would ${r.verdict.mode === "ask" ? "ask you first" : "send autonomously"}`
        : `⛔ blocked — ${r.verdict.reason}`),
    ),
  });

  const eff = c.enabled
    ? `${personaName} may start conversations ${c.scope === "all" ? "in all its chats" : "in opted-in chats"}, ${c.mode === "ask" ? "asking you first" : "on its own"}, ${minToTime(c.active_start_min)}–${minToTime(c.active_end_min)} WITA, ≤${c.max_per_chat}/chat/day (≤${c.max_per_day}/day total).`
    : `${personaName} will NOT initiate any messages.`;

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3 space-y-2.5">
      <div className="flex items-center gap-3">
        <Megaphone className={cn("size-4 flex-shrink-0", c.enabled ? "text-accent" : "text-fg-subtle")} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg">Proactivity</div>
          <p className="text-[11px] text-fg-subtle mt-0.5">Let this persona start conversations itself (birthday wishes, promised follow-ups) — never random chatter.</p>
        </div>
        <button
          type="button" role="switch" aria-checked={c.enabled} disabled={mut.isPending}
          onClick={() => set({ enabled: !c.enabled })}
          className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ring-focus disabled:opacity-50", c.enabled ? "bg-accent" : "bg-surface-elevated border border-border")}
          aria-label={c.enabled ? "Disable proactivity" : "Enable proactivity"}
        >
          <span className={cn("inline-block size-4 transform rounded-full bg-white shadow transition-transform", c.enabled ? "translate-x-6" : "translate-x-1")} />
        </button>
      </div>

      {c.enabled && (
        <div className="space-y-2.5 pl-7">
          <label className="flex items-center justify-between gap-2 text-xs">
            <span className="text-fg-muted">Mode</span>
            <select value={c.mode} onChange={(e) => set({ mode: e.target.value as "ask" | "auto" })} className="h-7 text-xs w-40">
              <option value="ask">ask me first</option>
              <option value="auto">send autonomously</option>
            </select>
          </label>
          <label className="flex items-center justify-between gap-2 text-xs">
            <span className="text-fg-muted">Chats</span>
            <select value={c.scope} onChange={(e) => set({ scope: e.target.value as "optin" | "all" })} className="h-7 text-xs w-40">
              <option value="optin">only where I opt in</option>
              <option value="all">all my routed chats</option>
            </select>
          </label>
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-fg-muted">Limits</span>
            <div className="flex items-center gap-1 text-fg-subtle">
              <input type="number" min={1} value={c.max_per_chat} onChange={(e) => set({ max_per_chat: Math.max(1, +e.target.value || 1) })} className="h-7 w-12 text-xs text-center" />/chat ·
              <input type="number" min={1} value={c.max_per_day} onChange={(e) => set({ max_per_day: Math.max(1, +e.target.value || 1) })} className="h-7 w-12 text-xs text-center" />/day ·
              <input type="number" min={0} value={c.cooldown_min} onChange={(e) => set({ cooldown_min: Math.max(0, +e.target.value || 0) })} className="h-7 w-14 text-xs text-center" />m apart
            </div>
          </div>
          <label className="flex items-center justify-between gap-2 text-xs">
            <span className="text-fg-muted">Active hours (WITA)</span>
            <span className="flex items-center gap-1">
              <input type="time" value={minToTime(c.active_start_min)} onChange={(e) => set({ active_start_min: timeToMin(e.target.value) })} className="h-7 text-xs" />–
              <input type="time" value={minToTime(c.active_end_min)} onChange={(e) => set({ active_end_min: timeToMin(e.target.value) })} className="h-7 text-xs" />
            </span>
          </label>

          {/* Test now — dry run, never sends */}
          <div className="flex items-center gap-2 pt-1">
            <select value={testChat} onChange={(e) => { setTestChat(e.target.value); setTestResult(null); }} className="h-7 text-xs flex-1 min-w-0">
              <option value="">{routes.length ? "test in chat…" : "(no chats routed to this persona)"}</option>
              {routes.map((r) => (
                <option key={r.name} value={r.name}>
                  {[chatLabel(r), r.channel_platform ?? r.match.platform, r.name].filter(Boolean).join(" · ")}
                </option>
              ))}
            </select>
            <button type="button" disabled={test.isPending || routes.length === 0} onClick={() => test.mutate()} className="h-7 px-2 text-xs rounded border border-border hover:bg-surface-2 disabled:opacity-40 inline-flex items-center gap-1 flex-shrink-0">
              <Sparkles className="size-3.5" /> Test now
            </button>
          </div>
          {testResult && <div className="text-[11px] text-fg">{testResult}</div>}
        </div>
      )}

      <p className="text-[11px] text-accent/90 pl-7">▸ {eff}</p>
    </div>
  );
}

function SandboxToggle({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["sandbox-overrides"],
    queryFn: () => BridgeApi.sandboxOverrides(),
  });
  const globalDefault = data?.global_default ?? false;
  const hasOverride = data ? personaName in data.overrides : false;
  const effective = data ? (data.overrides[personaName] ?? globalDefault) : globalDefault;

  const mut = useMutation({
    mutationFn: (enabled: boolean | null) => BridgeApi.setSandbox(personaName, enabled),
    onSuccess: (_r, enabled) => {
      qc.invalidateQueries({ queryKey: ["sandbox-overrides"] });
      toast.success(enabled === null ? "Reverted to fleet default" : enabled ? "Sandbox enabled" : "Sandbox disabled");
    },
    onError: (err) => toast.error("Toggle failed", String(err)),
  });
  const busy = mut.isPending || isLoading;

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3">
      <div className="flex items-center gap-3">
        <Shield className={cn("size-4 flex-shrink-0", effective ? "text-success" : "text-fg-subtle")} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg flex items-center gap-2">
            Sandboxed spawn
            <span className={cn("text-[10px]", hasOverride ? "text-warning" : "text-fg-subtle")}>
              {hasOverride ? "overridden" : `inheriting default (${globalDefault ? "on" : "off"})`}
            </span>
          </div>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            Run this persona's routed spawns inside bwrap — control plane hidden, no sudo, but full tools +
            network + <code className="font-mono">/data/ayana</code>. {hasOverride && (
              <button className="underline hover:text-fg" disabled={busy} onClick={() => mut.mutate(null)}>reset to default</button>
            )}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={effective}
          disabled={busy}
          onClick={() => mut.mutate(!effective)}
          className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ring-focus disabled:opacity-50",
            effective ? "bg-success" : "bg-surface-elevated border border-border",
          )}
          aria-label={effective ? "Disable sandbox" : "Enable sandbox"}
        >
          <span className={cn("inline-block size-4 transform rounded-full bg-white shadow transition-transform", effective ? "translate-x-6" : "translate-x-1")} />
        </button>
      </div>
      {effective && <MountsManager personaName={personaName} />}
    </div>
  );
}

// Extra host paths bound into this persona's sandbox — for a backend agent's
// own auth token / API-key dir / config that the masks would otherwise hide.
function MountsManager({ personaName }: { personaName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["persona-mounts", personaName], queryFn: () => BridgeApi.personaMounts(personaName) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["persona-mounts", personaName] });
  return (
    <div className="mt-3 pt-3 border-t border-border/60">
      <MountsEditor
        own={data?.mounts ?? []}
        inherited={data?.inherited ?? []}
        hint="persona tier — overrides global + agent; use void to cancel an inherited mount"
        onAdd={(path, mode) => BridgeApi.addMount(personaName, path, mode).then(invalidate).catch((e) => toast.error("Add failed", String(e)))}
        onRemove={(path) => BridgeApi.removeMount(personaName, path).then(invalidate).catch((e) => toast.error("Remove failed", String(e)))}
      />
    </div>
  );
}

function PersonaEditor({
  entryName,
  persona,
  isCreating,
  agentNames,
  existingNames,
  onSaved,
  onDelete,
  onClose,
}: {
  entryName: string;
  persona: Persona;
  isCreating: boolean;
  agentNames: string[];
  existingNames: string[];
  onSaved: (name: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(entryName);
  const [draft, setDraft] = useState<Persona>(persona);
  const [mentionsText, setMentionsText] = useState(persona.mention_names.join(", "));
  const [tab, setTab] = useState<"edit" | "diff" | "soul" | "history">("edit");

  useEffect(() => {
    setName(entryName);
    setDraft(persona);
    setMentionsText(persona.mention_names.join(", "));
  }, [entryName, isCreating]);

  const after: Persona = {
    ...draft,
    mention_names: mentionsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  };

  const dirty = useMemo(
    () => name !== entryName || JSON.stringify(after) !== JSON.stringify(persona),
    [name, entryName, after, persona],
  );

  const save = useMutation({
    mutationFn: async () => {
      if (!name) throw new Error("name required");
      if (isCreating && existingNames.includes(name)) throw new Error(`persona "${name}" already exists`);
      if (!isCreating && name !== entryName) {
        // True rename: FK ON UPDATE CASCADE rewires every route pointing
        // at this persona. Then save the (possibly-edited) body.
        await BridgeApi.renamePersona(entryName, name);
        await BridgeApi.savePersona(name, after);
      } else {
        await BridgeApi.savePersona(name, after);
      }
    },
    onSuccess: () => {
      toast.success(`Persona "${name}" saved`);
      onSaved(name);
    },
    onError: (err) => toast.error("Save failed", String(err)),
  });

  return (
    <Tabs.Root value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="flex-1 flex flex-col min-h-0">
      <div className="px-5 pt-4 pb-2 border-b border-border flex items-start justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-fg">{isCreating ? "New persona" : entryName}</h2>
          {!isCreating && (
            <p className="text-xs text-fg-muted mt-1">
              agent: <span className="font-mono text-fg">{persona.agent}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isCreating && (
            <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete">
              <Trash2 className="size-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}><XIcon className="size-3.5" /></Button>
        </div>
      </div>

      <Tabs.List className="border-b border-border px-5 py-1.5 flex items-center gap-3 text-xs font-medium">
        <TabBtn value="edit">Edit</TabBtn>
        <TabBtn value="diff" disabled={!dirty}>Diff</TabBtn>
        {!isCreating && <TabBtn value="soul">SOUL.md</TabBtn>}
        {!isCreating && <TabBtn value="history">History</TabBtn>}
      </Tabs.List>

      <div className="flex-1 overflow-y-auto">
        <Tabs.Content value="edit" className="px-5 py-4 space-y-4 outline-none">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full font-mono" />
          </Field>
          <Field label="Agent">
            <select value={draft.agent} onChange={(e) => setDraft({ ...draft, agent: e.target.value })} className="w-full">
              <option value="">(choose)</option>
              {agentNames.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Mention names (comma-separated)">
            <input
              value={mentionsText}
              onChange={(e) => setMentionsText(e.target.value)}
              className="w-full"
              placeholder="alfred, alf"
            />
            <p className="text-[10px] text-fg-subtle mt-1">
              Group routes with <code className="font-mono">mention_required</code> match when one of these appears in the text.
            </p>
            {after.mention_names.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {after.mention_names.map((m) => (
                  <Pill key={m} tone="info">@{m}</Pill>
                ))}
              </div>
            )}
          </Field>

          {!isCreating && <SpritePanel personaName={entryName} />}
          {!isCreating && <TuiToggle personaName={entryName} />}
          {!isCreating && <SandboxToggle personaName={entryName} />}
          {!isCreating && <DelegateToggle personaName={entryName} />}
          {!isCreating && <EscalateToggle personaName={entryName} />}
          {!isCreating && <CapabilitiesPanel personaName={entryName} />}
          {!isCreating && <InlineToolsPanel personaName={entryName} />}
          {!isCreating && <ProactivityPanel personaName={entryName} />}
        </Tabs.Content>

        <Tabs.Content value="diff" className="px-5 py-4 outline-none">
          <JsonDiff before={persona} after={after} />
        </Tabs.Content>

        {!isCreating && (
          <Tabs.Content value="soul" className="px-5 py-4 outline-none">
            <SoulEditor personaName={entryName} />
          </Tabs.Content>
        )}

        {!isCreating && (
          <Tabs.Content value="history" className="px-5 py-4 outline-none">
            <ObjectHistory objectType="persona" objectName={entryName} onReverted={() => onSaved(entryName)} />
          </Tabs.Content>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
        {dirty && <span className="mr-auto text-xs text-warning">unsaved changes</span>}
        <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || !name} loading={save.isPending}>
          <Save className="size-3.5" />
          {isCreating ? "Create" : "Save"}
        </Button>
      </div>
    </Tabs.Root>
  );
}

function SoulEditor({ personaName }: { personaName: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.bridge.personaPrompt(personaName),
    queryFn: () => BridgeApi.personaPrompt(personaName),
  });
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");

  useEffect(() => {
    if (data) {
      setContent(data.content);
      setOriginal(data.content);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => BridgeApi.savePersonaPrompt(personaName, content),
    onSuccess: () => {
      setOriginal(content);
      qc.invalidateQueries({ queryKey: qk.bridge.personaPrompt(personaName) });
      toast.success("SOUL.md saved");
    },
    onError: (err) => toast.error("Save failed", String(err)),
  });

  if (isLoading) return <CenteredSpinner />;
  const dirty = content !== original;

  return (
    <div className="space-y-3">
      <p className="text-xs text-fg-muted">
        Persistent prompt for this persona. Stored at <code className="font-mono">/data/agent-fleet/{personaName}/SOUL.md</code>.
      </p>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={20}
        className="w-full font-mono text-xs"
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>
          <Save className="size-3.5" />
          Save SOUL.md
        </Button>
      </div>
    </div>
  );
}

function TabBtn({ value, children, disabled }: { value: string; children: React.ReactNode; disabled?: boolean }) {
  return (
    <Tabs.Trigger
      value={value}
      disabled={disabled}
      className={cn(
        "h-7 px-2 rounded text-fg-muted hover:text-fg data-[state=active]:text-fg data-[state=active]:bg-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
      )}
    >
      {children}
    </Tabs.Trigger>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">{label}</span>
      {children}
    </label>
  );
}
